import React, { useState, useEffect } from 'react';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Upload, CheckCircle } from 'lucide-react';

export default function ApplicationForm() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [colleges, setColleges] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [dropdownControls, setDropdownControls] = useState({});
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    college_id: '',
    admission_session: '',
    admission_type: '',
    course: '',
    specialization: '',
    student_name: '',
    father_name: '',
    mother_name: '',
    gender: '',
    dob: '',
    category: '',
    email: '',
    mobile: '',
    aadhaar_no: '',
    deb_id: '',
    apaar_abc_id: '',
    marital_status: '',
    employment_status: '',
    religion: '',
    alternate_email: '',
    alternate_mobile: '',
    address: '',
    pincode: '',
    post_office: '',
    district: '',
    state: ''
  });

  const [academicRecords, setAcademicRecords] = useState([
    { exam_name: '', subject: '', year: '', board_university: '', obtained_marks: '', max_marks: '', percentage_grade: '' }
  ]);

  const [files, setFiles] = useState({
    student_photo: null,
    student_signature: null,
    address_id_proof: null,
    abc_deb_screenshot: null,
    other_document: null,
    tenth_marksheet: null,
    twelfth_marksheet: null,
    graduation_marksheet: null
  });

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchColleges();
    fetchDropdownControls();
  }, []);

  const fetchColleges = async () => {
    try {
      const response = await axios.get(`${API_URL}/colleges`, {
        headers: getAuthHeaders()
      });
      setColleges(response.data);
    } catch (error) {
      toast.error('Failed to fetch colleges');
    }
  };

  const fetchDropdownControls = async () => {
    try {
      const response = await axios.get(`${API_URL}/dropdown-controls`, {
        headers: getAuthHeaders()
      });
      const controlsMap = {};
      response.data.forEach(control => {
        controlsMap[control.category] = control.values;
      });
      setDropdownControls(controlsMap);
    } catch (error) {
      console.error('Failed to fetch dropdown controls');
    }
  };

  const handleCollegeChange = (collegeId) => {
    const college = colleges.find(c => c.id === collegeId);
    setSelectedCollege(college);
    setFormData({
      ...formData,
      college_id: collegeId,
      admission_session: '',
      admission_type: '',
      course: '',
      specialization: ''
    });
  };

  const handleCourseChange = (course) => {
    setFormData({
      ...formData,
      course: course,
      specialization: ''
    });
  };

  const addAcademicRecord = () => {
    setAcademicRecords([...academicRecords, {
      exam_name: '',
      subject: '',
      year: '',
      board_university: '',
      obtained_marks: '',
      max_marks: '',
      percentage_grade: ''
    }]);
  };

  const removeAcademicRecord = (index) => {
    if (academicRecords.length <= 1) {
      toast.error('At least one academic record row must remain');
      return;
    }
    setAcademicRecords(academicRecords.filter((_, i) => i !== index));
  };

  const updateAcademicRecord = (index, field, value) => {
    const updated = [...academicRecords];
    updated[index][field] = value;
    setAcademicRecords(updated);
  };

  const handleFileChange = (field, file) => {
    if (file) {
      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        toast.error(`${field.replace(/_/g, ' ')} must be less than 2MB`);
        return;
      }
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only PDF, JPG, and PNG files are allowed');
        return;
      }
    }
    setFiles({ ...files, [field]: file });
  };

  const validateForm = () => {
    // Required fields validation
    const requiredFields = [
      'college_id', 'admission_session', 'admission_type', 'course', 'specialization',
      'student_name', 'father_name', 'mother_name', 'gender', 'dob', 'category',
      'email', 'mobile', 'apaar_abc_id', 'marital_status', 'employment_status',
      'religion', 'address', 'pincode', 'post_office', 'district', 'state'
    ];
    
    for (const field of requiredFields) {
      if (!formData[field]) {
        toast.error(`Please fill in ${field.replace(/_/g, ' ')}`);
        return false;
      }
    }

    // Academic records are optional - only validate if user has filled any data
    // No mandatory validation for academic records

    // Validate required files
    if (!files.student_photo) {
      toast.error('Student photo is required');
      return false;
    }
    if (!files.student_signature) {
      toast.error('Student signature is required');
      return false;
    }
    if (!files.address_id_proof) {
      toast.error('Address/ID proof is required');
      return false;
    }
    if (!files.abc_deb_screenshot) {
      toast.error('ABC/DEB screenshot is required');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      
      // Add all text fields
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key] || '');
      });

      // Add academic records as JSON
      const academicRecordsData = academicRecords.map(record => ({
        exam_name: record.exam_name,
        subject: record.subject,
        year: record.year,
        board_university: record.board_university,
        obtained_marks: record.obtained_marks,
        max_marks: record.max_marks,
        percentage_grade: record.percentage_grade,
        marksheet_file_id: null
      }));
      formDataToSend.append('academic_records', JSON.stringify(academicRecordsData));

      // Add all files
      Object.keys(files).forEach(key => {
        if (files[key]) {
          formDataToSend.append(key, files[key]);
        }
      });

      const response = await axios.post(`${API_URL}/applications`, formDataToSend, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`Application submitted successfully! Application No: ${response.data.application_number}`);
      navigate('/applications');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const FileUploadField = ({ id, label, required, field }) => (
    <div>
      <Label htmlFor={id}>{label} {required && '*'} (PDF/JPG/PNG, Max 2MB)</Label>
      <div className="flex items-center gap-2 mt-1">
        <Input
          id={id}
          type="file"
          accept="image/*,.pdf"
          onChange={(e) => handleFileChange(field, e.target.files[0])}
          className="flex-1"
          data-testid={`${field}-input`}
        />
        {files[field] && (
          <CheckCircle className="w-5 h-5 text-green-500" />
        )}
      </div>
      {files[field] && (
        <p className="text-xs text-green-600 mt-1">{files[field].name}</p>
      )}
    </div>
  );

  return (
    <Sidebar>
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Apply for Admission
          </h1>
          <p className="text-slate-600 mt-2">Fill all mandatory fields marked with *</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Admission Details */}
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 border-b pb-2">1. Admission Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="college">University / College *</Label>
                <Select value={formData.college_id} onValueChange={handleCollegeChange}>
                  <SelectTrigger id="college" data-testid="college-select">
                    <SelectValue placeholder="Select College" />
                  </SelectTrigger>
                  <SelectContent>
                    {colleges.map(college => (
                      <SelectItem key={college.id} value={college.id}>{college.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedCollege && (
                <>
                  <div>
                    <Label htmlFor="admission_session">Admission Session *</Label>
                    <Select value={formData.admission_session} onValueChange={(value) => setFormData({...formData, admission_session: value})}>
                      <SelectTrigger id="admission_session" data-testid="session-select">
                        <SelectValue placeholder="Select Session" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedCollege.sessions.map(session => (
                          <SelectItem key={session} value={session}>{session}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="admission_type">Admission Type *</Label>
                    <Select value={formData.admission_type} onValueChange={(value) => setFormData({...formData, admission_type: value})}>
                      <SelectTrigger id="admission_type" data-testid="type-select">
                        <SelectValue placeholder="Select Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedCollege.admission_types.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="course">Course *</Label>
                    <Select value={formData.course} onValueChange={handleCourseChange}>
                      <SelectTrigger id="course" data-testid="course-select">
                        <SelectValue placeholder="Select Course" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedCollege.courses.map(course => (
                          <SelectItem key={course} value={course}>{course}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="specialization">Specialization / Subject *</Label>
                    <Input
                      id="specialization"
                      value={formData.specialization}
                      onChange={(e) => setFormData({...formData, specialization: e.target.value})}
                      placeholder="Enter specialization or subject"
                      data-testid="specialization-input"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {selectedCollege && (
            <>
              {/* Personal Details */}
              <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 border-b pb-2">2. Personal Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="student_name">Student Name *</Label>
                    <Input
                      id="student_name"
                      value={formData.student_name}
                      onChange={(e) => setFormData({...formData, student_name: e.target.value})}
                      data-testid="student-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="father_name">Father's Name *</Label>
                    <Input
                      id="father_name"
                      value={formData.father_name}
                      onChange={(e) => setFormData({...formData, father_name: e.target.value})}
                      data-testid="father-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mother_name">Mother's Name *</Label>
                    <Input
                      id="mother_name"
                      value={formData.mother_name}
                      onChange={(e) => setFormData({...formData, mother_name: e.target.value})}
                      data-testid="mother-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="gender">Gender *</Label>
                    <Select value={formData.gender} onValueChange={(value) => setFormData({...formData, gender: value})}>
                      <SelectTrigger id="gender" data-testid="gender-select">
                        <SelectValue placeholder="Select Gender" />
                      </SelectTrigger>
                      <SelectContent>
                        {(dropdownControls.genders || ['Male', 'Female', 'Other']).map(gender => (
                          <SelectItem key={gender} value={gender}>{gender}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="dob">Date of Birth (DD-MM-YYYY) *</Label>
                    <Input
                      id="dob"
                      value={formData.dob}
                      onChange={(e) => setFormData({...formData, dob: e.target.value})}
                      placeholder="15-06-2005"
                      data-testid="dob-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                      <SelectTrigger id="category" data-testid="category-select">
                        <SelectValue placeholder="Select Category" />
                      </SelectTrigger>
                      <SelectContent>
                        {(dropdownControls.categories || ['General', 'OBC', 'SC', 'ST']).map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      data-testid="email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mobile">Mobile (WhatsApp) *</Label>
                    <Input
                      id="mobile"
                      value={formData.mobile}
                      onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                      placeholder="+91-9876543210"
                      data-testid="mobile-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="aadhaar_no">Aadhaar Number</Label>
                    <Input
                      id="aadhaar_no"
                      value={formData.aadhaar_no}
                      onChange={(e) => setFormData({...formData, aadhaar_no: e.target.value})}
                      data-testid="aadhaar-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="deb_id">DEB ID</Label>
                    <Input
                      id="deb_id"
                      value={formData.deb_id}
                      onChange={(e) => setFormData({...formData, deb_id: e.target.value})}
                      data-testid="deb-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="apaar_abc_id">APAAR/ABC ID *</Label>
                    <Input
                      id="apaar_abc_id"
                      value={formData.apaar_abc_id}
                      onChange={(e) => setFormData({...formData, apaar_abc_id: e.target.value})}
                      data-testid="apaar-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="marital_status">Marital Status *</Label>
                    <Select value={formData.marital_status} onValueChange={(value) => setFormData({...formData, marital_status: value})}>
                      <SelectTrigger id="marital_status" data-testid="marital-select">
                        <SelectValue placeholder="Select Status" />
                      </SelectTrigger>
                      <SelectContent>
                        {(dropdownControls.marital_status || ['Single', 'Married']).map(status => (
                          <SelectItem key={status} value={status}>{status}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="employment_status">Employment Status *</Label>
                    <Select value={formData.employment_status} onValueChange={(value) => setFormData({...formData, employment_status: value})}>
                      <SelectTrigger id="employment_status" data-testid="employment-select">
                        <SelectValue placeholder="Select Status" />
                      </SelectTrigger>
                      <SelectContent>
                        {(dropdownControls.employment_status || ['Employed', 'Unemployed', 'Student']).map(status => (
                          <SelectItem key={status} value={status}>{status}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="religion">Religion *</Label>
                    <Select value={formData.religion} onValueChange={(value) => setFormData({...formData, religion: value})}>
                      <SelectTrigger id="religion" data-testid="religion-select">
                        <SelectValue placeholder="Select Religion" />
                      </SelectTrigger>
                      <SelectContent>
                        {(dropdownControls.religions || ['Hindu', 'Muslim', 'Christian', 'Sikh']).map(religion => (
                          <SelectItem key={religion} value={religion}>{religion}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="alternate_email">Alternate Email</Label>
                    <Input
                      id="alternate_email"
                      type="email"
                      value={formData.alternate_email}
                      onChange={(e) => setFormData({...formData, alternate_email: e.target.value})}
                      data-testid="alt-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="alternate_mobile">Alternate Mobile</Label>
                    <Input
                      id="alternate_mobile"
                      value={formData.alternate_mobile}
                      onChange={(e) => setFormData({...formData, alternate_mobile: e.target.value})}
                      data-testid="alt-mobile-input"
                    />
                  </div>
                </div>
              </div>

              {/* Address Details */}
              <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 border-b pb-2">3. Address Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="address">Full Address *</Label>
                    <Textarea
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      rows={3}
                      data-testid="address-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pincode">Pincode *</Label>
                    <Input
                      id="pincode"
                      value={formData.pincode}
                      onChange={(e) => setFormData({...formData, pincode: e.target.value})}
                      data-testid="pincode-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="post_office">Post Office *</Label>
                    <Input
                      id="post_office"
                      value={formData.post_office}
                      onChange={(e) => setFormData({...formData, post_office: e.target.value})}
                      data-testid="post-office-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="district">District *</Label>
                    <Input
                      id="district"
                      value={formData.district}
                      onChange={(e) => setFormData({...formData, district: e.target.value})}
                      data-testid="district-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                      data-testid="state-input"
                    />
                  </div>
                </div>
              </div>

              {/* Academic Details */}
              <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center mb-4 border-b pb-2">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">4. Academic Details</h2>
                    <p className="text-sm text-slate-500 mt-1">(Optional - Fill if applicable)</p>
                  </div>
                  <Button type="button" onClick={addAcademicRecord} variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Row
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="border border-slate-200 px-2 py-2 text-left text-xs font-medium text-slate-700">Exam Name</th>
                        <th className="border border-slate-200 px-2 py-2 text-left text-xs font-medium text-slate-700">Subject</th>
                        <th className="border border-slate-200 px-2 py-2 text-left text-xs font-medium text-slate-700">Year</th>
                        <th className="border border-slate-200 px-2 py-2 text-left text-xs font-medium text-slate-700">Board/University</th>
                        <th className="border border-slate-200 px-2 py-2 text-left text-xs font-medium text-slate-700">Obtained</th>
                        <th className="border border-slate-200 px-2 py-2 text-left text-xs font-medium text-slate-700">Max</th>
                        <th className="border border-slate-200 px-2 py-2 text-left text-xs font-medium text-slate-700">% / Grade</th>
                        <th className="border border-slate-200 px-2 py-2 text-left text-xs font-medium text-slate-700">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {academicRecords.map((record, index) => (
                        <tr key={index}>
                          <td className="border border-slate-200 px-2 py-2">
                            <Input
                              value={record.exam_name}
                              onChange={(e) => updateAcademicRecord(index, 'exam_name', e.target.value)}
                              className="min-w-[120px]"
                              placeholder="e.g., High School"
                              data-testid={`exam-name-${index}`}
                            />
                          </td>
                          <td className="border border-slate-200 px-2 py-2">
                            <Input
                              value={record.subject}
                              onChange={(e) => updateAcademicRecord(index, 'subject', e.target.value)}
                              className="min-w-[120px]"
                              data-testid={`subject-${index}`}
                            />
                          </td>
                          <td className="border border-slate-200 px-2 py-2">
                            <Select
                              value={record.year}
                              onValueChange={(value) => updateAcademicRecord(index, 'year', value)}
                            >
                              <SelectTrigger className="min-w-[100px]">
                                <SelectValue placeholder="Year" />
                              </SelectTrigger>
                              <SelectContent>
                                {(dropdownControls.academic_years || ['2020', '2021', '2022', '2023', '2024', '2025']).map(year => (
                                  <SelectItem key={year} value={year}>{year}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="border border-slate-200 px-2 py-2">
                            <Input
                              value={record.board_university}
                              onChange={(e) => updateAcademicRecord(index, 'board_university', e.target.value)}
                              className="min-w-[120px]"
                              data-testid={`board-${index}`}
                            />
                          </td>
                          <td className="border border-slate-200 px-2 py-2">
                            <Input
                              value={record.obtained_marks}
                              onChange={(e) => updateAcademicRecord(index, 'obtained_marks', e.target.value)}
                              className="min-w-[80px]"
                              data-testid={`obtained-${index}`}
                            />
                          </td>
                          <td className="border border-slate-200 px-2 py-2">
                            <Input
                              value={record.max_marks}
                              onChange={(e) => updateAcademicRecord(index, 'max_marks', e.target.value)}
                              className="min-w-[80px]"
                              data-testid={`max-${index}`}
                            />
                          </td>
                          <td className="border border-slate-200 px-2 py-2">
                            <Input
                              value={record.percentage_grade}
                              onChange={(e) => updateAcademicRecord(index, 'percentage_grade', e.target.value)}
                              className="min-w-[80px]"
                              data-testid={`percentage-${index}`}
                            />
                          </td>
                          <td className="border border-slate-200 px-2 py-2 text-center">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeAcademicRecord(index)}
                              className="text-red-600 hover:text-red-700"
                              disabled={academicRecords.length <= 1}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Document Uploads */}
              <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 border-b pb-2">5. Document Uploads</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FileUploadField id="student_photo" label="Student Photo" required field="student_photo" />
                  <FileUploadField id="student_signature" label="Student Signature" required field="student_signature" />
                  <FileUploadField id="address_id_proof" label="Address/ID Proof" required field="address_id_proof" />
                  <FileUploadField id="abc_deb_screenshot" label="ABC/DEB Screenshot" required field="abc_deb_screenshot" />
                  <FileUploadField id="other_document" label="Other Document" required={false} field="other_document" />
                </div>
                
                <h3 className="text-lg font-medium text-slate-800 mt-6 mb-4">Marksheet Uploads (Optional)</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FileUploadField id="tenth_marksheet" label="10th Marksheet" required={false} field="tenth_marksheet" />
                  <FileUploadField id="twelfth_marksheet" label="12th Marksheet" required={false} field="twelfth_marksheet" />
                  <FileUploadField id="graduation_marksheet" label="Graduation Marksheet" required={false} field="graduation_marksheet" />
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-4 pt-4">
                <Button type="button" variant="outline" onClick={() => navigate('/applications')}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-application-button"
                  className="bg-[#0052CC] hover:bg-[#0043A8] text-white"
                >
                  {loading ? 'Submitting...' : 'Submit Application'}
                </Button>
              </div>
            </>
          )}
        </form>
      </div>
    </Sidebar>
  );
}
